import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from './lib/utils.js';

export type DimeWizardStepStatus = 'complete' | 'current' | 'upcoming' | 'issues';

export type DimeWizardStep = {
  id: string;
  title: string;
  subtitle?: string;
  status: DimeWizardStepStatus;
  /** Rendered as text beside the title; colour alone would not reach a screen reader. */
  issueCount?: number;
};

type DimeWizardStepperProps = {
  steps: readonly DimeWizardStep[];
  /** Supplying this makes reached steps navigable; upcoming steps stay inert either way. */
  onStepSelect?: (stepId: string) => void;
  /** Shows only `N of M · Title` over a segmented bar, for a phone-width screen; reached segments follow `onStepSelect`. */
  compact?: boolean;
  className?: string;
};

function StepMarker({ step, index }: Readonly<{ step: DimeWizardStep; index: number }>) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-medium',
        step.status === 'current' && 'bg-primary text-primary-foreground',
        step.status === 'complete' && 'bg-primary/10 text-primary',
        step.status === 'issues' && 'bg-destructive/10 text-destructive',
        step.status === 'upcoming' && 'bg-muted text-muted-foreground',
      )}
    >
      {step.status === 'complete' ? <CheckIcon className="size-4" /> : index + 1}
    </span>
  );
}

function StepLabel({ step }: Readonly<{ step: DimeWizardStep }>) {
  const issueLabel =
    step.status === 'issues' && step.issueCount != null
      ? `${step.issueCount} ${step.issueCount === 1 ? 'issue' : 'issues'}`
      : null;

  return (
    <span className="flex min-w-0 flex-col text-left">
      <span
        className={cn(
          'truncate text-sm font-medium',
          step.status === 'upcoming' && 'text-muted-foreground',
        )}
      >
        {step.title}
      </span>
      {issueLabel ? (
        <span className="truncate text-xs text-destructive">{issueLabel}</span>
      ) : (
        step.subtitle && (
          <span className="truncate text-xs text-muted-foreground">{step.subtitle}</span>
        )
      )}
    </span>
  );
}

function issueText(step: DimeWizardStep) {
  if (step.issueCount == null) return 'needs attention';
  return `${step.issueCount} ${step.issueCount === 1 ? 'issue' : 'issues'}`;
}

function isStepReached(step: DimeWizardStep) {
  return step.status !== 'upcoming' && step.status !== 'current';
}

function CompactStepper({
  steps,
  onStepSelect,
  className,
}: Readonly<{
  steps: readonly DimeWizardStep[];
  onStepSelect?: (stepId: string) => void;
  className?: string;
}>) {
  const currentIndex = Math.max(
    0,
    steps.findIndex((step) => step.status === 'current'),
  );
  const current = steps[currentIndex];
  const stepsWithIssues = steps.filter((step) => step.status === 'issues');

  return (
    <div className={cn('flex flex-col gap-2', className)} data-slot="wizard-stepper">
      <p className="text-sm font-medium">
        {`${currentIndex + 1} of ${steps.length} · ${current?.title ?? ''}`}
      </p>
      <ol aria-label="Progress" className="flex gap-1">
        {steps.map((step, index) => {
          const name = `${step.title}${step.status === 'issues' ? `, ${issueText(step)}` : ''}`;
          const bar = (
            <span
              aria-hidden
              className={cn(
                'block h-1 w-full rounded-full',
                index <= currentIndex ? 'bg-primary' : 'bg-muted',
                step.status === 'issues' && 'bg-destructive',
              )}
            />
          );

          return (
            <li
              key={step.id}
              aria-current={index === currentIndex ? 'step' : undefined}
              aria-label={name}
              data-slot="wizard-stepper-segment"
              data-filled={index <= currentIndex}
              className="flex-1"
            >
              {onStepSelect != null && isStepReached(step) ? (
                <button
                  type="button"
                  aria-label={`Go to step ${index + 1} of ${steps.length}: ${name}`}
                  onClick={() => onStepSelect(step.id)}
                  className="flex h-11 w-full cursor-pointer items-center rounded-md focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
                >
                  {bar}
                </button>
              ) : (
                bar
              )}
            </li>
          );
        })}
      </ol>
      {stepsWithIssues.map((step) => (
        <p key={step.id} className="text-xs text-destructive">
          {`${step.title}: ${issueText(step)}`}
        </p>
      ))}
    </div>
  );
}

export function DimeWizardStepper({
  steps,
  onStepSelect,
  compact = false,
  className,
}: Readonly<DimeWizardStepperProps>) {
  if (compact) {
    return <CompactStepper steps={steps} onStepSelect={onStepSelect} className={className} />;
  }

  return (
    <ol
      aria-label="Progress"
      className={cn('flex flex-wrap items-center gap-x-2 gap-y-3', className)}
      data-slot="wizard-stepper"
    >
      {steps.map((step, index) => {
        const navigable = onStepSelect != null && isStepReached(step);
        const content = (
          <>
            <StepMarker step={step} index={index} />
            <StepLabel step={step} />
          </>
        );

        return (
          <React.Fragment key={step.id}>
            <li
              aria-current={step.status === 'current' ? 'step' : undefined}
              className="flex min-w-0 flex-1 items-center gap-3"
            >
              {navigable ? (
                <button
                  type="button"
                  onClick={() => onStepSelect(step.id)}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-md text-left focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
                >
                  {content}
                </button>
              ) : (
                content
              )}
            </li>
            {index < steps.length - 1 && (
              <ChevronRightIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}
